import { useEffect, useState, useCallback } from "react";
import { LifeBuoy, Inbox, Check, X, Trash2, RefreshCw, Mail } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const STATUSES = [
  { v: "", label: "All" },
  { v: "open", label: "Open" },
  { v: "responded", label: "Responded" },
  { v: "closed", label: "Closed" },
];

const STATUS_COLOR = {
  open: "#FDE047",
  responded: "#7DD3FC",
  closed: "#86EFAC",
};

export default function AdminSupport() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await api.get("/support/tickets", { params });
      setItems(r.data);
    } catch (e) {
      toast.error("Could not load tickets");
    } finally { setBusy(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/support/tickets/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Could not update"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this ticket?")) return;
    try {
      await api.delete(`/support/tickets/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  const open = items.filter((t) => t.status === "open").length;

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admin · Support</p>
          <h1 className="font-display text-4xl font-black mt-1 flex items-center gap-3">
            Support inbox
            {open > 0 && <span className="nb-tag bg-pink-300 text-sm">{open} open</span>}
          </h1>
          <p className="text-base font-medium text-slate-600 mt-2">Tickets submitted via the public support form.</p>
        </div>
        <button onClick={load} className="nb-btn-secondary inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" strokeWidth={2.5}/> Refresh</button>
      </header>

      <div className="flex flex-wrap gap-2 mb-5">
        {STATUSES.map((s) => (
          <button key={s.v || "all"} onClick={() => setFilter(s.v)} data-testid={`filter-${s.v || 'all'}`}
            className={`nb-tag text-sm ${filter === s.v ? "bg-yellow-300" : "bg-white"}`}>{s.label}</button>
        ))}
      </div>

      {busy ? (
        <div className="nb-card p-12 text-center"><p className="font-bold">Loading…</p></div>
      ) : items.length === 0 ? (
        <div className="nb-card p-12 text-center">
          <Inbox className="w-10 h-10 mx-auto" strokeWidth={2.5}/>
          <p className="font-display text-xl font-bold mt-3">No tickets yet</p>
          <p className="text-sm text-slate-600">When students submit support requests, they'll show up here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((t) => (
            <article key={t.id} className="nb-card p-5" data-testid={`ticket-${t.ticket_number}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="nb-tag font-mono text-xs">{t.ticket_number}</span>
                    <span className="nb-tag text-xs" style={{ background: STATUS_COLOR[t.status] || "#FFFFFF" }}>{t.status}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold mt-2">{t.subject}</h3>
                  <p className="text-sm font-medium text-slate-700 mt-1 inline-flex items-center gap-2">
                    <Mail className="w-4 h-4" strokeWidth={2.5}/> {t.name} · <a href={`mailto:${t.email}?subject=Re: ${t.ticket_number}`} className="underline">{t.email}</a>
                  </p>
                </div>
                <span className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-slate-800 mt-3 whitespace-pre-wrap leading-relaxed border-l-4 border-yellow-300 pl-4">{t.message}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {t.status !== "responded" && <button onClick={() => setStatus(t.id, "responded")} className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"><Check className="w-3 h-3" strokeWidth={2.5}/> Mark responded</button>}
                {t.status !== "closed" && <button onClick={() => setStatus(t.id, "closed")} className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"><X className="w-3 h-3" strokeWidth={2.5}/> Close</button>}
                <button onClick={() => del(t.id)} className="nb-btn-danger text-xs inline-flex items-center gap-1 py-2 px-3"><Trash2 className="w-3 h-3" strokeWidth={2.5}/> Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
